import { z } from 'zod'

/**
 * How bad a tree's problem is, 1–5.
 *
 * **Anchored on risk to people and property, not on how damaged the tree is.**
 * Those come apart constantly — a hollow trunk in the middle of a field is a
 * severe defect with almost no urgency, while a small dead limb over a
 * playground gate is a minor defect that needs doing this week. A scale that
 * conflates the two is one nobody can pick a number on.
 *
 * Two boundaries carry the weight, and both are answerable while standing under
 * the tree:
 *
 * - **1 → 2** is "does this generate work, or just a note?" That is what makes
 *   level 1 useful: it builds a watch list rather than padding a work list.
 * - **4 → 5** is "can this wait for the normal cycle?"
 *
 * A fixed ladder rather than a configurable one, unlike `TreeSpecies`. Species
 * are a fact about this business; a severity ladder is shared vocabulary, and
 * one that varied per install could not be compared across two sites or counted
 * across a portfolio. Same reasoning as `JOB_LEVELS`.
 *
 * Deliberately NOT presented as ISA TRAQ. That is a real four-level standard
 * (Low / Moderate / High / Extreme) derived from a likelihood-by-consequence
 * matrix, and stretching it to five while borrowing its name would misrepresent
 * a certification the arborists using this may actually hold.
 */
export const PROBLEM_SEVERITIES = [1, 2, 3, 4, 5] as const

/**
 * The ladder the picker offers, as the schema that accepts it.
 *
 * One list. The rungs used to be written out again as a union of literals,
 * so a rung added to the picker could be refused by every input that embeds
 * this schema, and a rung dropped from the picker could still be stored.
 */
export const problemSeveritySchema = z.literal(PROBLEM_SEVERITIES)

export type ProblemSeverity = z.infer<typeof problemSeveritySchema>

/**
 * One word each, because these sit in a picker on a phone and beside a tree
 * number in a list.
 *
 * "Monitor" is a verb among four adjectives, and that inconsistency is
 * deliberate: level 1 is the only one that means "no work", so it reads as a
 * different kind of answer — which is exactly what it is.
 */
export const PROBLEM_SEVERITY_LABELS: Record<ProblemSeverity, string> = {
  1: 'Monitor',
  2: 'Minor',
  3: 'Moderate',
  4: 'Serious',
  5: 'Critical',
}

/** The sentence under each label, for the picker and for a legend. */
export const PROBLEM_SEVERITY_DESCRIPTIONS: Record<ProblemSeverity, string> = {
  1: 'Recorded so someone looks again. No work needed.',
  2: 'A real defect, but no meaningful risk yet.',
  3: 'Should be worked in the normal cycle.',
  4: 'Credible risk to people or property — prioritize it.',
  5: 'Immediate hazard; do not wait for the normal cycle.',
}

/**
 * Narrow a stored integer to a severity, or null.
 *
 * The column is an `Int` so it orders naturally for sorting and counting,
 * Postgres has no declarative check constraint through Prisma, and the 1-5
 * bound is enforced by this file's Zod schema on the way in. That leaves the
 * read path as the one spot where a row written by some future path could hold
 * a 9.
 *
 * Out-of-range reads as "not rated" rather than being clamped. A severity is a
 * judgment somebody made; clamping a 9 to a 5 would invent the most alarming
 * judgment available on their behalf, and null is the honest answer for a
 * value nobody can account for.
 *
 * Lives here rather than in a converter because two of them now need it — the
 * tree DTO and the proposal snapshot — and two copies of a rule about which
 * numbers are real is exactly the pair that drifts.
 */
export function toProblemSeverity(value: number | null): ProblemSeverity | null {
  if (value === null) return null
  const parsed = problemSeveritySchema.safeParse(value)
  return parsed.success ? parsed.data : null
}
